import { Badge, Btn, Card, IconPdf, IconPlay, IconPrint, IconVideo } from '../ui';
import type { BriefingTopic } from './dailyBriefingData';
import { formatBriefingDate, isPdfBriefingTopic } from './dailyBriefingData';

function TopicMediaIcon({ topic, iconClasses }: { topic: BriefingTopic; iconClasses?: string }) {
    if (topic.type === 'video') {
        return <IconVideo iconClasses={iconClasses} />;
    }
    if (isPdfBriefingTopic(topic)) {
        return <IconPdf iconClasses={iconClasses} />;
    }
    return <IconPrint iconClasses={iconClasses} />;
}

interface TodaysBriefingCardProps {
    topic: BriefingTopic | null;
    confirmedCount: number;
    crewCount: number;
    clearing?: boolean;
    onLaunch: () => void;
    onChangeTopic: () => void;
    onScheduleTopics: () => void;
    onClearTopic: () => void;
    onViewConfirmations: () => void;
}

export function TodaysBriefingCard({
    topic,
    confirmedCount,
    crewCount,
    clearing = false,
    onLaunch,
    onChangeTopic,
    onScheduleTopics,
    onClearTopic,
    onViewConfirmations,
}: TodaysBriefingCardProps) {
    const isPdf = topic ? isPdfBriefingTopic(topic) : false;
    const launchLabel = topic
        ? isPdf
            ? `View ${topic.title} PDF`
            : `Launch & display ${topic.title}`
        : '';
    const primaryActionText = isPdf ? 'View PDF' : 'Launch & Display';
    const primaryActionIcon = isPdf ? <IconPdf /> : <IconPlay />;

    return (
        <Card variant="card-lg" cardClasses="db-today-card">
            <div className="db-today-top">
                <span className="db-today-label">Today&apos;s Briefing</span>
                <span className="db-today-date">{formatBriefingDate()}</span>
            </div>

            {topic ? (
                <>
                    <div className="db-today-main">
                        <Btn
                            variant="icon"
                            ariaLabel={launchLabel}
                            buttonIcon={<TopicMediaIcon topic={topic} iconClasses="db-today-media-icon" />}
                            buttonOnClick={onLaunch}
                            buttonClasses="db-today-icon"
                        />
                        <div className="db-today-info">
                            <h2 className="db-today-title">{topic.title}</h2>
                            <div className="db-today-meta">
                                <span className="db-today-meta-item">
                                    <span aria-hidden="true">
                                        <TopicMediaIcon topic={topic} iconClasses="db-meta-icon" />
                                    </span>
                                    {topic.style}
                                </span>
                                {!isPdf && <span className="db-today-meta-item">Duration —</span>}
                            </div>
                        </div>
                        <div className="db-today-stats">
                            <div className="db-today-fraction">
                                {confirmedCount} / {crewCount}
                            </div>
                            <div className="db-today-stats-label">crew confirmed today</div>
                        </div>
                    </div>
                    <div className="db-today-actions">
                        <Btn
                            variant="warning"
                            buttonText={primaryActionText}
                            buttonIcon={primaryActionIcon}
                            buttonOnClick={onLaunch}
                            buttonClasses="db-launch-btn"
                        />
                        <div className="db-today-secondary-actions">
                            <Btn
                                variant="secondary-outlined"
                                buttonText="Change Today's Topic"
                                buttonOnClick={onChangeTopic}
                                buttonClasses="db-today-ghost-btn"
                                buttonDisabled={clearing}
                            />
                            <Btn
                                variant="secondary-outlined"
                                buttonText="Schedule Topics"
                                buttonOnClick={onScheduleTopics}
                                buttonClasses="db-today-ghost-btn"
                                buttonDisabled={clearing}
                            />
                            <Btn
                                variant="secondary-outlined"
                                buttonText={clearing ? 'Clearing…' : "Clear Today's Topic"}
                                buttonOnClick={onClearTopic}
                                buttonClasses="db-today-ghost-btn"
                                buttonDisabled={clearing}
                            />
                            <Btn
                                variant="secondary-outlined"
                                buttonText="View Confirmations"
                                buttonOnClick={onViewConfirmations}
                                buttonClasses="db-today-ghost-btn"
                                buttonDisabled={clearing}
                            />
                        </div>
                    </div>
                </>
            ) : (
                <div className="db-today-empty">
                    <div className="db-today-empty-copy">
                        <h2 className="db-today-title">No topic assigned for today</h2>
                        <p>Choose a topic from your library so crews see a briefing on login.</p>
                    </div>
                    <div className="db-today-actions">
                        <div className="db-today-secondary-actions">
                            <Btn
                                variant="warning"
                                buttonText="Choose Today's Topic"
                                buttonOnClick={onChangeTopic}
                            />
                            <Btn
                                variant="secondary-outlined"
                                buttonText="Schedule Topics"
                                buttonOnClick={onScheduleTopics}
                                buttonClasses="db-today-ghost-btn"
                            />
                        </div>
                        <Btn
                            variant="secondary-outlined"
                            buttonText="View Confirmations"
                            buttonOnClick={onViewConfirmations}
                            buttonClasses="db-today-ghost-btn"
                        />
                    </div>
                </div>
            )}
        </Card>
    );
}

export function StyleBadge({ style }: { style: BriefingTopic['style'] }) {
    const badgeClass =
        style === 'Video' ? 'db-style-badge db-style-badge--video' : style === 'Poster' ? 'db-style-badge db-style-badge--poster' : 'db-style-badge db-style-badge--pdf';
    return <Badge badgeText={style} badgeClasses={badgeClass} />;
}
